import { GenericThresholdOptions, StepOptions } from '../';

export interface MergeThresholdsOptions {
  /**
   * The panel max value used when the panel is in `Percent` mode and the dynamic
   * steps were emitted in `Absolute` mode. Used to convert dynamic absolute
   * values to percentages so they sit on the same numeric scale as panel steps.
   */
  panelMax?: number;
}

/**
 * Merge panel-defined thresholds with query-driven dynamic thresholds.
 */
export function mergeThresholds<T extends GenericThresholdOptions<StepOptions> | undefined>(
  panel: T,
  dynamic: ReadonlyArray<GenericThresholdOptions<StepOptions> | undefined>,
  options: MergeThresholdsOptions = {}
): T {
  const dynamicSteps: StepOptions[] = [];
  for (const block of dynamic) {
    if (!block?.steps?.length) continue;
    const fromMode = block.mode ?? 'Absolute';
    for (const step of block.steps) {
      const adjusted = adjustStepForPanelMode(step, fromMode, panel?.mode, options.panelMax);
      if (adjusted === undefined) continue;
      dynamicSteps.push(adjusted);
    }
  }

  if (dynamicSteps.length === 0) return panel;

  const panelSteps = panel?.steps ?? [];
  const byValue = new Map<number, StepOptions>();
  const order: number[] = [];

  for (const step of panelSteps) {
    if (!byValue.has(step.value)) {
      order.push(step.value);
    }
    byValue.set(step.value, step);
  }

  for (const step of dynamicSteps) {
    const existing = byValue.get(step.value);
    if (existing === undefined) {
      byValue.set(step.value, step);
      order.push(step.value);
      continue;
    }
    if (isPlaceholderStep(existing)) {
      byValue.set(step.value, step);
    }
  }

  const merged = order.map((v) => byValue.get(v)!);
  merged.sort((a, b) => a.value - b.value);

  // Spread panel (when present) to preserve mode/base/default_color and any
  // TimeSeries-specific extensions like scale_axis. The `as T` cast is required
  // because TS can't narrow a generic object spread back into T.
  return { ...panel, steps: merged } as T;
}

/**
 * A placeholder step is one that only carries a `value` with no
 * color/name/operator metadata. It exists to force threshold rendering
 * defaults (e.g. the gauge fallback `{ value: 0 }`) and should not shadow a
 * dynamic threshold at the same value that carries real metadata.
 */
function isPlaceholderStep(step: StepOptions): boolean {
  return step.color === undefined && step.name === undefined && step.operator === undefined;
}

/**
 * Convert a dynamic step from its emitted mode (always `Absolute` from the
 * runtime wrapper today) into the panel's mode. Returns `undefined` when the
 * conversion would silently produce wrong values (e.g. Percent panel with no
 * known max).
 */
function adjustStepForPanelMode(
  step: StepOptions,
  fromMode: 'Percent' | 'Absolute',
  toMode: 'Percent' | 'Absolute' | undefined,
  panelMax: number | undefined
): StepOptions | undefined {
  if (toMode === undefined || toMode === fromMode) return step;
  if (toMode === 'Percent' && fromMode === 'Absolute') {
    if (panelMax === undefined || panelMax === 0) return undefined;
    return { ...step, value: (step.value / panelMax) * 100 };
  }
  if (toMode === 'Absolute' && fromMode === 'Percent') {
    if (panelMax === undefined) return undefined;
    return { ...step, value: (step.value / 100) * panelMax };
  }
  return step;
}
